// src/ui/overlays/RoundTimer.tsx — P3 GAME-07 / UI-03.
//
// Top-centre HUD chip that renders the remaining round time as `mm:ss` when
// round.phase === 'active'. Renders null during headstart (the
// HeadstartCountdown sibling occupies the same slot) and during the 'ended'
// phase (TeamSwitchOverlay owns the slot while the diegetic team-switch
// transition plays).
//
// WARNING-15 tier-isolation: this component subscribes ONLY to the renderBus
// 'sim-tick' payload. It does not import from src/sim/** and never calls into
// sim-tier functions. GameScene is the single emitter and bundles all fields
// the HUD needs per tick.
//
// Source: Phase 3 UI-SPEC §RoundTimer (this file's positioning, copywriting,
// palette, aria-attributes); PATTERNS.md Table — RoundTimer analog is
// DevOverlay's renderBus subscription.

import { useEffect, useState, type JSX } from 'react';

import { renderBus } from '../../render/bus';

import './round-timer.css';

interface RoundTimerState {
  phase: 'headstart' | 'active' | 'ended';
  tick: number;
  endTick: number;
}

/** Format non-negative seconds as zero-padded `mm:ss`. */
function formatMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = (s % 60).toString().padStart(2, '0');
  return `${mm}:${ss}`;
}

export function RoundTimer(): JSX.Element | null {
  const [state, setState] = useState<RoundTimerState | null>(null);

  useEffect(() => {
    return renderBus.on('sim-tick', (e) => {
      setState({ phase: e.round.phase, tick: e.tick, endTick: e.round.endTick });
    });
  }, []);

  if (!state || state.phase !== 'active') return null;

  // Sim runs at 30 Hz — convert remaining ticks to seconds.
  const remainingTicks = Math.max(0, state.endTick - state.tick);
  const mmss = formatMMSS(remainingTicks / 30);

  return (
    <div
      className="round-timer"
      data-testid="round-timer"
      role="timer"
      aria-live="off"
      aria-label={`Round time remaining ${mmss}`}
      // Inline style mirrors round-timer.css so jsdom tests can assert the
      // numeric-alignment contract without loading the stylesheet (PATTERNS
      // Pattern E — matches the Joystick + TorchButton precedent).
      style={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {mmss}
    </div>
  );
}
